import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { queryClient } from './lib/queryClient.ts';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { initGlobalErrorHandlers } from './lib/globalErrorHandlers.ts';
import { initCsrf } from './lib/csrf.ts';
import { initChunkReload } from './lib/chunkReload.ts';

// Initialize Sentry for BetterStack error tracking
const sentryDsn = import.meta.env.VITE_BETTERSTACK_ERROR_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Capture 100% of errors
    sampleRate: 1.0,
    // Disable performance tracing (we don't need it for frontend)
    tracesSampleRate: 0,
  });
}

// Initialize global error handlers for BetterStack logging
initGlobalErrorHandlers();

// Recover automatically when a tab left open across a deploy asks for a chunk
// that no longer exists
initChunkReload();

// Initialize CSRF protection (non-blocking)
initCsrf();

// Remove initial loader with fade animation
const loader = document.getElementById('initial-loader');
if (loader) {
  loader.classList.add('fade-out');
  setTimeout(() => loader.remove(), 200);
}

// A data router, so pages can use useBlocker and the other data-router APIs.
// One splat route renders the whole app; routes.tsx keeps declaring the real
// routes with <Routes>, which work unchanged underneath it. The ErrorBoundary
// sits inside the route element too: a data router otherwise catches render
// errors in its own default boundary, before ours ever sees them.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <ErrorBoundary>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ErrorBoundary>
    ),
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
